'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { useTheme } from '@/contexts/ThemeContext'
import { getSupabaseClient } from '@/utils/supabase/singleton'
import { useAuthStore } from '@/store/useAuthStore'

interface InstructorProfile {
  id: string
  display_name: string
  bio: string | null
  profile_image_url: string | null
  certification_agency: string
  certification_level: string
  years_experience: number
  main_region: string
  specialties: string[]
  is_verified: boolean
  average_rating: number
  review_count: number
  likes_count: number
  is_liked?: boolean
}

const REGIONS = ['전체', '서울/경기', '부산/경남', '대구/경북', '인천', '대전/충청', '광주/전라', '제주', '강원', '해외']

function Avatar({ name, isVerified, size = 48, isDark, imageUrl }: { name: string; isVerified: boolean; size?: number; isDark: boolean; imageUrl?: string | null }) {
  const ring = isVerified ? { boxShadow: `0 0 0 2px #fff, 0 0 0 3.5px ${isDark ? '#5ea6ff' : '#2b6df0'}` } : {}
  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={name}
        className="flex-shrink-0 rounded-full object-cover"
        style={{ width: size, height: size, ...ring }}
        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
      />
    )
  }
  return (
    <div
      className="flex items-center justify-center font-bold flex-shrink-0 rounded-full"
      style={{
        width: size,
        height: size,
        background: isDark ? '#1e2d45' : '#d6e4f7',
        color: isDark ? '#5ea6ff' : '#143560',
        fontSize: size * 0.38,
        ...ring,
      }}
    >
      {name.charAt(0)}
    </div>
  )
}

export default function InstructorsPage() {
  const router = useRouter()
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  const { user } = useAuthStore()

  const [instructors, setInstructors] = useState<InstructorProfile[]>([])
  const [filteredInstructors, setFilteredInstructors] = useState<InstructorProfile[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedRegion, setSelectedRegion] = useState('전체')

  useEffect(() => { loadInstructors() }, [])
  useEffect(() => { filterInstructors() }, [instructors, searchQuery, selectedRegion])

  const loadInstructors = async () => {
    try {
      const supabase = getSupabaseClient()
      const { data, error } = await supabase
        .from('instructor_profiles')
        .select('*')
        .neq('is_active', false)
        .order('created_at', { ascending: false })
      if (error) throw error
      setInstructors(data || [])
    } catch (e) {
      console.error('Failed to load instructors:', e)
    } finally {
      setIsLoading(false)
    }
  }

  const filterInstructors = () => {
    let filtered = [...instructors]
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      filtered = filtered.filter(i =>
        i.display_name.toLowerCase().includes(q) ||
        i.bio?.toLowerCase().includes(q) ||
        i.certification_agency.toLowerCase().includes(q)
      )
    }
    if (selectedRegion !== '전체') {
      filtered = filtered.filter(i => i.main_region === selectedRegion)
    }
    setFilteredInstructors(filtered)
  }

  const bg = isDark ? '#0f172a' : '#f4f7fb'
  const ink = isDark ? '#e8f0fb' : '#0a1f3a'
  const secondary = isDark ? '#94a3b8' : '#6b7684'
  const accent = isDark ? '#5ea6ff' : '#2b6df0'
  const card = isDark ? '#1e2a3d' : '#ffffff'
  const border = isDark ? '#2c3a52' : '#e9eff7'

  const featured = filteredInstructors[0]
  const rest = filteredInstructors.slice(1)

  return (
    <div style={{ background: bg, minHeight: '100vh' }}>

      {/* 헤더 */}
      <div className="px-5 pt-8 pb-4 max-w-2xl mx-auto">
        <h1 style={{ font: '700 22px/30px sans-serif', color: ink, margin: '0 0 4px' }}>강사</h1>
        <p style={{ font: '400 13px/20px sans-serif', color: secondary, margin: 0 }}>
          검증된 프리다이빙 강사 {instructors.length}명
        </p>
      </div>

      {/* 검색 */}
      <div className="px-5 mb-4 max-w-2xl mx-auto">
        <div
          className="flex items-center gap-2 px-3 py-2.5 rounded-xl"
          style={{ background: card, border: `1px solid ${border}` }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={secondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
          </svg>
          <input
            type="text"
            placeholder="강사명, 자격증, 전문분야 검색"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{
              background: 'transparent', border: 'none', outline: 'none',
              font: '400 14px/22px sans-serif', color: ink, flex: 1,
            }}
          />
        </div>
      </div>

      {/* 지역 칩 필터 */}
      <div className="chip-rail px-5 mb-6 max-w-2xl mx-auto">
        {REGIONS.map(r => (
          <button
            key={r}
            onClick={() => setSelectedRegion(r)}
            className="flex-shrink-0 px-3 py-1.5 rounded-full text-sm transition-all"
            style={{
              background: selectedRegion === r
                ? (isDark ? '#1e3a5f' : '#0a1f3a')
                : (isDark ? '#1e2a3d' : '#ffffff'),
              color: selectedRegion === r ? '#ffffff' : secondary,
              border: `1px solid ${selectedRegion === r ? 'transparent' : border}`,
              font: '500 12px/18px sans-serif',
            }}
          >
            {r}
          </button>
        ))}
      </div>

      <div className="px-5 pb-16 max-w-2xl mx-auto">
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="rounded-2xl p-4 animate-pulse" style={{ background: card, height: 88 }} />
            ))}
          </div>
        ) : filteredInstructors.length === 0 ? (
          <div className="rounded-2xl p-10 text-center" style={{ background: card, border: `1px solid ${border}` }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🤿</div>
            <p style={{ font: '600 15px/22px sans-serif', color: ink, marginBottom: 6 }}>
              {searchQuery ? `"${searchQuery}"에 해당하는 강사가 없습니다` : '아직 등록된 강사가 없습니다'}
            </p>
            <p style={{ font: '400 13px/20px sans-serif', color: secondary, marginBottom: 16 }}>
              {searchQuery ? '다른 검색어를 시도해보세요' : '첫 번째 강사가 되어보세요!'}
            </p>
            {!searchQuery && (
              <a
                href="/for-instructors"
                style={{ display: 'inline-block', padding: '10px 20px', borderRadius: 12, background: accent, color: '#fff', font: '600 14px/20px sans-serif', textDecoration: 'none' }}
              >
                강사 등록하기 →
              </a>
            )}
          </div>
        ) : (
          <>
            {/* 피처드 카드 (첫 번째 강사) */}
            {featured && (
              <Link
                href={`/instructors/${featured.id}`}
                className="block rounded-2xl p-5 mb-3 transition-all"
                style={{ background: isDark ? '#1e2d45' : '#0a1f3a' }}
              >
                <div className="flex items-start gap-4">
                  <Avatar name={featured.display_name} isVerified={featured.is_verified} size={56} isDark={isDark} imageUrl={featured.profile_image_url} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span style={{ font: '700 16px/24px sans-serif', color: '#fff' }}>
                        {featured.display_name}
                      </span>
                      {featured.is_verified && (
                        <span
                          className="px-1.5 py-0.5 rounded text-xs"
                          style={{ background: 'rgba(43,109,240,0.3)', color: '#93c5ff', font: '500 11px/16px sans-serif' }}
                        >
                          인증
                        </span>
                      )}
                    </div>
                    <p style={{ font: '400 12px/18px sans-serif', color: 'rgba(255,255,255,0.6)', margin: '0 0 8px' }}>
                      {featured.certification_agency} · {featured.main_region}
                      {featured.years_experience > 0 ? ` · ${featured.years_experience}년` : ''}
                    </p>
                    {featured.bio && (
                      <p
                        className="line-clamp-2"
                        style={{ font: '400 13px/20px sans-serif', color: 'rgba(255,255,255,0.8)', margin: 0 }}
                      >
                        {featured.bio}
                      </p>
                    )}
                    <div className="flex items-center gap-4 mt-3">
                      {featured.average_rating > 0 && (
                        <span style={{ font: '500 12px/18px sans-serif', color: '#fbbf24', fontVariantNumeric: 'tabular-nums' }}>
                          ★ {featured.average_rating.toFixed(1)}
                        </span>
                      )}
                      {featured.review_count > 0 && (
                        <span style={{ font: '400 12px/18px sans-serif', color: 'rgba(255,255,255,0.5)', fontVariantNumeric: 'tabular-nums' }}>
                          후기 {featured.review_count}건
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            )}

            {/* 나머지 강사 목록 */}
            <div className="space-y-2">
              {rest.map(inst => (
                <Link
                  key={inst.id}
                  href={`/instructors/${inst.id}`}
                  className="flex items-center gap-3 rounded-2xl p-4 transition-all"
                  style={{ background: card, border: `1px solid ${border}` }}
                >
                  <Avatar name={inst.display_name} isVerified={inst.is_verified} size={44} isDark={isDark} imageUrl={inst.profile_image_url} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span style={{ font: '600 14px/22px sans-serif', color: ink }} className="truncate">
                        {inst.display_name}
                      </span>
                      {inst.is_verified && (
                        <span
                          className="flex-shrink-0 px-1.5 py-0.5 rounded"
                          style={{
                            background: isDark ? '#1e3a5f' : '#e9eff7',
                            color: isDark ? '#5ea6ff' : '#2b6df0',
                            font: '500 10px/14px sans-serif',
                          }}
                        >
                          인증
                        </span>
                      )}
                    </div>
                    <p style={{ font: '400 12px/18px sans-serif', color: secondary, margin: 0 }}>
                      {inst.certification_agency} · {inst.main_region}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    {inst.average_rating > 0 && (
                      <span style={{ font: '600 13px/20px sans-serif', color: isDark ? '#fbbf24' : '#d97706', fontVariantNumeric: 'tabular-nums', display: 'block' }}>
                        ★ {inst.average_rating.toFixed(1)}
                      </span>
                    )}
                    {inst.review_count > 0 && (
                      <span style={{ font: '400 11px/16px sans-serif', color: secondary, fontVariantNumeric: 'tabular-nums' }}>
                        후기 {inst.review_count}
                      </span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
